import { streamQuantityEventName } from '@/components/commonComponents/ExportByColumnModal';
import {
  AmiliHealthOrderStatus,
  OrderStatus,
  PhysicalCondition,
  TemperatureCondition,
} from '@/enums';
import {
  CreateOrderEntity,
  OrderEntity,
  OrderParams,
  OrderStatusEntity,
  UpdateOrderEntity,
} from '@/pages/privatePages/order/types';
import { newQueryKeys } from '@/queries/queryKeys';
import { QueryFunctionContext } from '@tanstack/react-query';
import APIService from './axios.service';
import { BaseService } from './base.service';

export interface OrderCountParams {
  channelIds: string[];
  startDate?: string;
  endDate?: string;
  orderStatuses?: (OrderStatus | AmiliHealthOrderStatus)[];
}

class OrderServiceClass extends BaseService<
  'order',
  OrderEntity,
  CreateOrderEntity
> {
  constructor() {
    super('orders');
  }

  createOrder = (payload: CreateOrderEntity): Promise<OrderEntity[]> => {
    return APIService.post(this.path, payload);
  };

  updateOrder = (payload: UpdateOrderEntity): Promise<OrderEntity> => {
    const { id, ...rest } = payload;

    return APIService.put(this.path + '/' + id, rest);
  };

  streamOrder = (params: OrderParams): Promise<any> => {
    return APIService.get(this.path + '/stream-orders', {
      params,
      responseType: 'stream',
      onDownloadProgress(data) {
        const responseData = data.event?.target?.response || '';
        // Regex to find all progress values
        const regex = /"progress"\s*:\s*(\d+(\.\d+)?)/g;
        const matches = [...responseData.matchAll(regex)];
        // Get the last match
        const lastMatch = matches[matches.length - 1];
        const lastProgress = lastMatch
          ? Math.round(parseFloat(lastMatch[1]))
          : 0;

        // Dispatch kit quantity for percent progress bar
        window.dispatchEvent(
          new CustomEvent(streamQuantityEventName, {
            detail: { percentProgress: lastProgress },
          }),
        );
      },
    });
  };

  getOrderHistory(
    context: QueryFunctionContext<
      ReturnType<(typeof newQueryKeys)['order']['list']>
    >,
  ): Promise<OrderStatusEntity[]> {
    const { params } = context.queryKey[0];
    return APIService.get(`orders/${params?.id}/history`);
  }

  updateMultipleOrdersStatus = (payload: {
    ids?: string[];
    orderStatus: string;
    orderIds?: string[];
    currentStatus?: string;
    orderConditionOnReceived?: {
      temperature: TemperatureCondition;
      physical: PhysicalCondition;
    };
  }): Promise<unknown> => {
    return APIService.patch(this.path + `/multiple-order-status`, payload);
  };

  count = (params: OrderCountParams): Promise<number> => {
    return APIService.get(this.path + '/count', {
      params,
    });
  };
}
export const orderService = new OrderServiceClass();
